import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellRing, Building2, Check, ChevronsUpDown, Clock, Shield, Wallet } from 'lucide-react';
import { AppShell, MenuItem } from '@ui/components/layout/Shell';
import { cn } from '@ui/lib/cn';
import { minutesLeft, money } from '@ui/lib/format';
import { ROLE_LABEL } from '@ui/lib/status';
import { ADMIN_APP_URL } from '@ui/lib/urls';
import { useAppDispatch } from '@ui/store';
import { api } from '@ui/store/baseApi';
import { useDashboardQuery, useExitOrgMutation, useMeQuery, useSwitchOrgMutation } from '@/store/orgApi';
import { useApprovalCountQuery } from '@/store/crmApi';
import { orgNav } from './nav';

/** The customer app's frame: shared shell plus organization switcher, wallet and live calls. */

function OrgSwitcher() {
  const { data: session } = useMeQuery();
  const [open, setOpen] = useState(false);
  const [switchOrg] = useSwitchOrgMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  if (!session?.organization) return null;
  const choose = async (id: string) => {
    setOpen(false);
    await switchOrg({ organizationId: id }).unwrap();
    dispatch(api.util.resetApiState());
    navigate('/');
  };
  return (
    <div ref={ref} className="relative px-2">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-2 text-left shadow-sm hover:bg-raised">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-brand-soft text-brand-ink">
          <Building2 className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{session.organization.name}</span>
          <span className="block text-2xs text-muted">{ROLE_LABEL[session.role ?? ''] ?? session.role}</span>
        </span>
        <ChevronsUpDown className="h-4 w-4 text-muted" />
      </button>
      {open && session.organizations.length > 0 && (
        <div className="absolute left-2 right-2 top-full z-30 mt-1 rounded-lg border border-line bg-surface p-1 shadow-pop">
          {session.organizations.map((o) => (
            <button key={o.id} onClick={() => void choose(o.id)} className="flex w-full items-center justify-between rounded px-2.5 py-1.5 text-sm hover:bg-raised">
              <span className="truncate">{o.name}</span>
              {o.id === session.organization?.id && <Check className="h-4 w-4 text-brand" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ApprovalsChip() {
  const { data } = useApprovalCountQuery(undefined, { pollingInterval: 60_000 });
  if (!data?.pending) return null;
  return (
    <Link to="/crm/approvals" className="hidden items-center gap-1.5 rounded-full border border-warn/30 bg-warn-soft px-2.5 py-1 text-xs font-medium text-warn sm:inline-flex">
      <BellRing className="h-3.5 w-3.5" />
      {data.pending} to approve
    </Link>
  );
}

function TopbarChips() {
  const { data } = useDashboardQuery({ days: 1 }, { pollingInterval: 60_000 });
  const { data: session } = useMeQuery();
  if (!data) return null;
  // Minute plan: minutes left (low under an hour), otherwise the wallet.
  const low = data.minutes ? data.minutes.balanceSeconds < 3600 : data.wallet.balanceMicros < 5_000_000;
  return (
    <>
      {session?.organization?.modules?.crm && <ApprovalsChip />}
      {data.liveCalls > 0 && (
        <Link to="/calls?status=IN_PROGRESS" className="hidden items-center gap-1.5 rounded-full border border-good/25 bg-good-soft px-2.5 py-1 text-xs font-medium text-good sm:inline-flex">
          <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-good" />
          {data.liveCalls} live
        </Link>
      )}
      <Link to="/billing" className={cn('hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium sm:inline-flex', low ? 'border-warn/30 bg-warn-soft text-warn' : 'border-line bg-surface text-ink-2 hover:bg-raised')}>
        {data.minutes ? <Clock className="h-3.5 w-3.5" /> : <Wallet className="h-3.5 w-3.5" />}
        <span className="tabular">{data.minutes ? `${minutesLeft(data.minutes.balanceSeconds)} min` : money(data.wallet.balanceMicros, data.wallet.currency)}</span>
      </Link>
    </>
  );
}

export function WebShell() {
  const { data: session } = useMeQuery();
  const [exitOrg] = useExitOrgMutation();
  return (
    <AppShell
      nav={orgNav(session?.organization?.modules)}
      sidebarTop={<OrgSwitcher />}
      topbarExtras={<TopbarChips />}
      liveEventsPath={session?.organization ? '/api/dashboard/events' : null}
      profilePath="/settings/profile"
      banner={
        <>
          {session?.organization?.emergencyStop && (
            <div className="bg-bad px-4 py-1.5 text-center text-xs font-medium text-white">
              Emergency stop is on: campaigns, outbound calls, texts, emails and lead playbooks are on hold. Inbound calls are still answered.{' '}
              <Link to="/settings" className="underline">Settings</Link>
            </div>
          )}
          {session?.impersonating && (
            <div className="bg-warn-soft px-4 py-1.5 text-center text-xs font-medium text-warn">
              You are viewing {session.organization?.name} as a platform admin. Every change is recorded in the audit log.
            </div>
          )}
        </>
      }
      menuItems={(close) =>
        session?.user.isPlatformAdmin && (
          <MenuItem
            icon={Shield}
            onClick={async () => {
              close();
              if (session.impersonating) await exitOrg().unwrap().catch(() => undefined);
              window.location.assign(ADMIN_APP_URL);
            }}
          >
            Platform admin
          </MenuItem>
        )
      }
    />
  );
}
